import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Url } from "../function/FunctionApi";
import { getCookie } from "../../Pages/Login/Login";
import useShowInvoice from "../../hooks/invoice/ShowInvoice";

const authHeaders = () => ({
  Authorization: `Bearer ${JSON.parse(getCookie("user"))?.token}`,
});

function ReciveInvoicesContent() {
  // const { showInvoice } = useShowInvoice();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [categories, setCategories] = useState([]);

  // Search filters
  const [filters, setFilters] = useState({
    name: "",
    from: "",
    to: "",
    categoryId: "",
    status: "",
  });

    // Export to CSV function
  const exportToCSV = () => {
    if (invoices.length === 0) {
      alert("لا توجد بيانات للتصدير");
      return;
    }

    // Get all unique categories from invoices
    const allCategories = new Set();
    invoices.forEach(invoice => {
      invoice.invoiceCategories?.forEach(cat => {
        if (cat.category?.name) {
          allCategories.add(cat.category.name);
        }
      });
    });

    // Format the data for export with categories
    const formattedData = invoices.map(invoice => {
      // Extract nested objects
      const governorateName = invoice.governorate?.name || '';
      const representativeName = invoice.representative?.fullName || '';
      
      // Format dates
      const invoiceDate = invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString('ar-EG') : '';
      
      // Create base object with essential fields
      const baseObj = {
        'رقم الفاتورة': invoice.invoiceCode || '',
        'تاريخ الفاتورة': invoiceDate,
        'العميل': invoice.customer || '',
        'المحافظة': governorateName, 
        'العنوان': invoice.address || '',
        'الهاتف': invoice.phone || '',
        'المندوب': representativeName,
        'الحالة': invoice.status || '',
        'الإجمالي': calculateInvoiceTotal(invoice.invoiceCategories).toFixed(2) || '0.00',
        'الشحن': invoice.shipping?.toFixed(2) || '0.00',
        'متأخر': invoice.isLate ? 'نعم' : 'لا',
        'عاجل': invoice.isUrgent ? 'نعم' : 'لا',
        'لم يتم الرد': invoice.notReplied ? 'نعم' : 'لا',
        'الصفحة': invoice.page || '',
        'الملاحظة': invoice.note || ''
      };
      
      // ================== 1 ==================

      // Add category quantities
      // allCategories.forEach(categoryName => {
      //   const category = invoice.invoiceCategories?.find(cat => cat.category?.name === categoryName);
      //   baseObj[categoryName] = category ? category.quantity : 0;
      // });

      // ================== 2 ==================

    //    allCategories.forEach(categoryName => {
    //   const categoryTotalQuantity =
    //     invoice.invoiceCategories
    //       ?.filter(cat => cat.category?.name === categoryName)
    //       .reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;

    //   baseObj[categoryName] = categoryTotalQuantity;
    // });

      allCategories.forEach(categoryName => {
        const categoryTotalQuantity =
          invoice.invoiceCategories
            ?.filter(
              cat =>
                cat.category?.name === categoryName &&
                cat.price > 0  
            )
            .reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;

        baseObj[categoryName] = categoryTotalQuantity;
      });

      return baseObj;
    });

    // Calculate total quantities for each category and grand total
    const categoryTotals = {};
    let grandTotal = 0;
    
    allCategories.forEach(categoryName => {
      const total = formattedData.reduce((sum, item) => sum + (parseFloat(item[categoryName]) || 0), 0);
      categoryTotals[categoryName] = total;
      grandTotal += total;
    });

    // Add a summary row at the bottom
    const summaryRow = {
      'رقم الفاتورة': 'الإجمالي',
      'تاريخ الفاتورة': '',
      'العميل': '',
      'المحافظة': '',
      'العنوان': '',
      'الهاتف': '',
      'المندوب': '',
      'الحالة': '',
      'الإجمالي': formattedData.reduce((sum, item) => sum + (parseFloat(item['الإجمالي']) || 0), 0).toFixed(2),
      'الشحن': formattedData.reduce((sum, item) => sum + (parseFloat(item['الشحن']) || 0), 0).toFixed(2),
      'متأخر': '',
      'عاجل': '',
      'لم يتم الرد': '',
      'الصفحة': '',
      'الملاحظة': '',
      ...categoryTotals,
      'إجمالي الكميات': grandTotal
    };

    // Add the summary row to the data
    formattedData.push(summaryRow);

    // Get headers from the first data object
    const headers = Object.keys(formattedData[0]);

    // Convert data to CSV format
    const csvContent = [
      headers.join(','),
      ...formattedData.map(row => 
        headers.map(header => `"${row[header] || ''}"`).join(',')
      )
    ].join('\n');

    // Create and download CSV file
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `فواتير_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Fetch categories for dropdown
  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${Url}Category/get-categories`);
      setCategories(response.data.data || []);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  // Fetch invoices from API
  const fetchInvoices = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();

      if (filters.name) params.append("name", filters.name);
      if (filters.from) params.append("from", filters.from);
      if (filters.to) params.append("to", filters.to);
      if (filters.categoryId) params.append("categoryId", filters.categoryId);
      if (filters.status) params.append("status", filters.status);

      const response = await axios.get(
        `${Url}Invoice/by-secretary?${params.toString()}`,
        {
          headers: authHeaders(),
        }
      );

      if (response.data.statusCode === 200) {
        setInvoices(response.data.data || []);
        setTotalCount(response.data.count || 0);
        setTotalAmount(response.data.totalAmount || 0);
      } else {
        setError(response.data.message || "حدث خطأ في جلب البيانات");
      }
    } catch (err) {
      setError(err.response?.data?.message || "حدث خطأ في الاتصال بالخادم");
      console.error("Error fetching invoices:", err);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
  fetchCategories();
  fetchInvoices();
}, []);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle search
  const handleSearch = () => {
    fetchInvoices();
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setFilters({
      name: "",
      from: "",
      to: "",
      categoryId: "",
      status: "",
    });
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("ar-EG");
  };

  // Get status color classes
  const getStatusColorClasses = (status) => {
    switch (status) {
      case "جديد":
        return "bg-indigo-600 text-white";
      case "تحت التجهيز":
        return "bg-yellow-100 text-yellow-900";
      case "تم التسليم":
        return "bg-green-100 text-green-900";
      case "مرتجع":
        return "bg-red-100 text-red-900";
      case "ملغي":
        return "bg-gray-100 text-gray-900";
      default:
        return "bg-gray-100 text-gray-900";
    }
  };

  // Calculate total for invoice
  const calculateInvoiceTotal = (invoiceCategories) => {
    return (
      invoiceCategories?.reduce((total, item) => {
        return total + (item.total || 0);
      }, 0) || 0
    );
  };

  return (
    <div className="p-2 sm:p-4 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-0">
            استلام الفواتير
          </h1>
          <button
            onClick={exportToCSV}
            disabled={invoices.length === 0 || loading}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed font-bold text-sm flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            تصدير CSV
          </button>
        </div>
        <div className="bg-white rounded-lg shadow p-3 border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="text-center">
              <p className="text-lg font-bold text-blue-900">{totalCount}</p>
              <p className="text-gray-900 font-bold text-xs">إجمالي الفواتير</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-green-900">
                {totalAmount.toLocaleString()} ج.م
              </p>
              <p className="text-gray-900 font-bold text-xs">إجمالي المبلغ</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-4 border border-gray-200">
        <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
          <span className="bg-blue-100 p-1 rounded mr-2">
            <svg
              className="w-4 h-4 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </span>
          فلاتر البحث
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 items-end">
          <div>
            <label className="block text-xs font-bold text-gray-900 mb-1">
              اسم السكرتريه
            </label>
            <input
              type="text"
              value={filters.name}
              onChange={(e) => handleFilterChange("name", e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-900 font-bold text-xs"
              placeholder="اسم السكرتريه"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-900 mb-1">
              من تاريخ
            </label>
            <input
              type="date"
              value={filters.from}
              onChange={(e) => handleFilterChange("from", e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-900 font-bold text-xs"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-900 mb-1">
              إلى تاريخ
            </label>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => handleFilterChange("to", e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-900 font-bold text-xs"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-900 mb-1">
              اختر صنف
            </label>
            <select
              value={filters.categoryId}
              onChange={(e) => handleFilterChange("categoryId", e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-900 font-bold text-xs"
            >
              <option value="">الكل</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-900 mb-1">
              الحالة
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-900 font-bold text-xs"
            >
              <option value="">الكل</option>
              <option value="جديد">جديد</option>
              <option value="تحت التجهيز">تحت التجهيز</option>
              <option value="تم_التسليم">تم التسليم</option>
              <option value="مرتجع">مرتجع</option>
              <option value="ملغي">ملغي</option>
            </select>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleSearch}
              disabled={loading}
              className="flex-1 bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed font-bold text-xs"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                  <span className="mr-1">جاري...</span>
                </div>
              ) : (
                "بحث"
              )}
            </button>
            <button
              onClick={handleClearFilters}
              className="flex-1 bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600 focus:outline-none focus:ring-1 focus:ring-gray-500 font-bold text-xs"
            >
              مسح
            </button>
            <button
              onClick={exportToCSV}
              disabled={invoices.length === 0 || loading}
              className="flex-1 bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 focus:outline-none focus:ring-1 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed font-bold text-xs"
            >
              تصدير CSV
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-3 rounded mb-3 shadow">
          <div className="flex items-center">
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="font-bold text-xs">{error}</span>
          </div>
        </div>
      )}

      {/* Invoices Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-1 py-1 text-right text-xs font-bold text-gray-900 uppercase">
                  الكود
                </th>
                <th className="hidden xs:table-cell px-1 py-1 text-right text-xs font-bold text-gray-900 uppercase">
                  التاريخ
                </th>
                <th className="px-1 py-1 text-right text-xs font-bold text-gray-900 uppercase">
                  العميل
                </th>
                <th className="hidden md:table-cell px-1 py-1 text-right text-xs font-bold text-gray-900 uppercase">
                  التليفون
                </th>
                <th className="hidden lg:table-cell px-1 py-1 text-right text-xs font-bold text-gray-900 uppercase">
                  المحافظة
                </th>
                <th className="hidden xl:table-cell px-1 py-1 text-right text-xs font-bold text-gray-900 uppercase">
                  العنوان
                </th>
                <th className="hidden sm:table-cell px-1 py-1 text-right text-xs font-bold text-gray-900 uppercase">
                  الصفحة
                </th>
                <th className="px-1 py-1 text-right text-xs font-bold text-gray-900 uppercase">
                  الحالة
                </th>
                <th className="hidden 2xl:table-cell px-1 py-1 text-right text-xs font-bold text-gray-900 uppercase">
                  الملاحظة
                </th>
                <th className="hidden md:table-cell px-1 py-1 text-right text-xs font-bold text-gray-900 uppercase">
                  الشحن
                </th>
                <th className="hidden xl:table-cell px-1 py-1 whitespace-nowrap">
                  <button
                    onClick={exportToCSV}
                    className="bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 focus:outline-none focus:ring-1 focus:ring-green-500 font-bold text-xs"
                  >
                    تصدير CSV
                  </button>
                </th>
                <th className="hidden xl:table-cell px-1 py-1 whitespace-nowrap">
                  مستعجل
                </th>
                <th className="hidden xl:table-cell px-1 py-1 whitespace-nowrap">
                  لم يتم الرد
                </th>
                <th className="px-1 py-1 text-right text-xs font-bold text-gray-900 uppercase">
                  الإجمالي
                </th>
                
                
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
                             {loading ? (
                 <tr>
                   <td colSpan={13} className="px-1 py-4 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mb-1"></div>
                      <span className="text-gray-900 font-bold text-xs">
                        جاري التحميل...
                      </span>
                    </div>
                  </td>
                </tr>
                             ) : invoices.length === 0 ? (
                 <tr>
                   <td colSpan={13} className="px-1 py-4 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <svg
                        className="w-6 h-6 text-gray-400 mb-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <span className="text-gray-900 font-bold text-xs">
                        لا توجد فواتير
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
                invoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-1 py-1 whitespace-nowrap text-xs font-bold text-gray-900">
                      {invoice.invoiceCode}
                    </td>
                    <td className="hidden xs:table-cell px-1 py-1 whitespace-nowrap text-xs font-bold text-gray-900">
                      {formatDate(invoice.invoiceDate)}
                    </td>
                    <td className="px-1 py-1 whitespace-nowrap text-xs font-bold text-gray-900 max-w-20 truncate">
                      {invoice.customer}
                    </td>
                    <td className="hidden md:table-cell px-1 py-1 whitespace-nowrap text-xs font-bold text-gray-900">
                      {invoice.phone}
                    </td>
                    <td className="hidden lg:table-cell px-1 py-1 whitespace-nowrap text-xs font-bold text-gray-900">
                      {invoice.governorate?.name || "-"}
                    </td>
                    <td className="hidden xl:table-cell px-1 py-1 text-xs font-bold text-gray-900 max-w-24 truncate">
                      {invoice.address}
                    </td>
                    <td className="hidden sm:table-cell px-1 py-1 whitespace-nowrap text-xs font-bold text-gray-900">
                      {invoice.page}
                    </td>
                    <td className="px-1 py-1 whitespace-nowrap">
                      <span
                        className={`inline-flex px-1 py-0.5 text-xs font-bold rounded ${getStatusColorClasses(
                          invoice.status
                        )}`}
                      >
                        {invoice.status}
                      </span>
                    </td>
                    <td className="hidden 2xl:table-cell px-1 py-1 text-xs font-bold text-gray-900 max-w-20 truncate">
                      {invoice.note || "-"}
                    </td>
                    <td className="hidden md:table-cell px-1 py-1 whitespace-nowrap text-xs font-bold text-gray-900">
                      {invoice.shipping.toFixed(2)}
                    </td>
                    <td className="hidden xl:table-cell px-1 py-1 whitespace-nowrap">
                      <span
                        className={`inline-flex px-1 py-0.5 text-xs font-bold rounded ${
                          invoice.isUrgent
                            ? "bg-yellow-500 text-white"
                            : "bg-gray-500 text-white"
                        }`}
                      >
                        {invoice.isUrgent ? "نعم" : "لا"}
                      </span>
                    </td>
                    <td className="hidden xl:table-cell px-1 py-1 whitespace-nowrap">
                      <span
                        className={`inline-flex px-1 py-0.5 text-xs font-bold rounded ${
                          invoice.notReplied
                            ? "bg-red-500 text-white"
                            : "bg-gray-500 text-white"
                        }`}
                      >
                        {invoice.notReplied ? "نعم" : "لا"}
                      </span>
                    </td>
                                         <td className="px-1 py-1 whitespace-nowrap text-xs font-bold text-green-900">
                       {calculateInvoiceTotal(invoice.invoiceCategories).toFixed(
                         2
                       )}
                     </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default ReciveInvoicesContent;
